import React, { useRef } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useMotionTemplate,
  useReducedMotion
} from 'framer-motion';

/* ---------------------------------------------------------------------------
 * Shared easing / variants
 * ------------------------------------------------------------------------- */

export const EASE = [0.22, 1, 0.36, 1];

export const fadeUp = (delay = 0, dist = 28) => ({
  hidden: { opacity: 0, y: dist },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE, delay }
  }
});

export const springPop = {
  hidden: { opacity: 0, scale: 0.86, y: 18 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 260, damping: 19 }
  }
};

export const staggerWrap = (stagger = 0.09, delayChildren = 0.05) => ({
  hidden: {},
  show: { transition: { staggerChildren: stagger, delayChildren } }
});

/* ---------------------------------------------------------------------------
 * <Reveal> — viewport-triggered entrance used for section headers & blocks
 * ------------------------------------------------------------------------- */

export const Reveal = ({
  children,
  delay = 0,
  y = 28,
  once = true,
  amount = 0.25,
  className,
  style,
  as = 'div',
  ...rest
}) => {
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration: 0.7, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* ---------------------------------------------------------------------------
 * <Magnetic> — element eases toward the pointer and springs back on leave
 * ------------------------------------------------------------------------- */

export const Magnetic = ({ children, strength = 0.32, className, style }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 14, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 14, mass: 0.4 });

  const onMove = (e) => {
    if (reduce || e.pointerType !== 'mouse' || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ display: 'inline-block', x: sx, y: sy, ...style }}
      onPointerMove={onMove}
      onPointerLeave={reset}
      onPointerUp={reset}
    >
      {children}
    </motion.div>
  );
};

/* ---------------------------------------------------------------------------
 * <TiltCard> — 3D tilt that follows the pointer, with a soft moving glare
 * ------------------------------------------------------------------------- */

export const TiltCard = ({
  children,
  className,
  style,
  max = 8,
  glare = true,
  ...rest
}) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 220, damping: 18 });
  const sry = useSpring(ry, { stiffness: 220, damping: 18 });
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  const glareBg = useMotionTemplate`radial-gradient(circle at ${gx}% ${gy}%, rgba(255,255,255,0.32), transparent 60%)`;

  const onMove = (e) => {
    if (reduce || e.pointerType !== 'mouse' || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;   // 0 → 1
    const py = (e.clientY - r.top) / r.height;   // 0 → 1
    ry.set((px - 0.5) * 2 * max);
    rx.set(-(py - 0.5) * 2 * max);
    gx.set(px * 100);
    gy.set(py * 100);
  };
  const reset = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        rotateX: srx,
        rotateY: sry,
        transformStyle: 'preserve-3d',
        transformPerspective: 900,
        ...style
      }}
      onPointerMove={onMove}
      onPointerLeave={reset}
      {...rest}
    >
      {children}
      {glare && !reduce && (
        <motion.span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            background: glareBg,
            opacity: 0,
            zIndex: 3
          }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      )}
    </motion.div>
  );
};
